import React, { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { CameraIcon } from 'lucide-react';

import { HomeIcon } from 'src/icons';
import { Flex } from 'src/components/flex';
import { EPropertyFormField, type PropertyForm } from 'src/components/create-or-update-property-modal/types';

import styles from 'src/components/create-or-update-property-modal/PropertyImageEditor.module.css';

type Props = {
    form: UseFormReturn<PropertyForm>;
    /** The currently persisted image, shown when no new file has been chosen. */
    currentImageUrl?: string | null;
    /** The newly chosen file (not yet uploaded), takes precedence over currentImageUrl. */
    selectedFile: File | null;
    onSelectFile: (file: File) => void;
};

function buildSummary(form: UseFormReturn<PropertyForm>): string {
    const type = form.watch(EPropertyFormField.Type);
    const beds = form.watch(EPropertyFormField.NumberOfBedrooms);
    const baths = form.watch(EPropertyFormField.NumberOfBathrooms);
    const rent = form.watch(EPropertyFormField.Rent);

    const parts: string[] = [];
    if (type) {
        parts.push(type);
    }
    if (beds !== undefined && beds !== '') {
        parts.push(`${beds} bed`);
    }
    if (baths !== undefined && baths !== '') {
        parts.push(`${baths} bath`);
    }
    if (rent !== undefined && rent !== '') {
        const rentNumber = Number(rent);
        const formattedRent = Number.isFinite(rentNumber) ? rentNumber.toLocaleString() : rent;
        parts.push(`$${formattedRent}/mo`);
    }
    return parts.join(' · ');
}

export default function PropertyImageEditor({ form, currentImageUrl, selectedFile, onSelectFile }: Props) {
    const previewUrl = useMemo(
        () => (selectedFile ? URL.createObjectURL(selectedFile) : null),
        [selectedFile],
    );

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const displayedUrl = previewUrl ?? currentImageUrl ?? undefined;

    const name = form.watch(EPropertyFormField.Name);
    const isVacant = form.watch(EPropertyFormField.IsVacant);
    const summary = buildSummary(form);

    return (
        <Flex alignItems="center" justifyContent="space-between" gap={2} className={styles.header}>
            <Flex alignItems="center" gap={2} className={styles.identity}>
                <div className={styles.wrapper}>
                    <Avatar src={displayedUrl} className={styles.avatar} variant="circular">
                        <HomeIcon />
                    </Avatar>
                    <IconButton
                        component="label"
                        size="small"
                        className={styles.editButton}
                        aria-label="Edit property image"
                        data-testid="edit-property-image-button"
                    >
                        <CameraIcon size={16} />
                        <input
                            type="file"
                            accept="image/jpeg, image/png, image/webp, image/jpg"
                            hidden
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    onSelectFile(file);
                                }
                                // reset so selecting the same file again re-triggers onChange
                                e.target.value = '';
                            }}
                        />
                    </IconButton>
                </div>

                <div className={styles.text}>
                    <Typography variant="h6" noWrap className={styles.name}>
                        {name || 'New Property'}
                    </Typography>
                    {summary && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {summary}
                        </Typography>
                    )}
                </div>
            </Flex>

            <FormControlLabel
                labelPlacement="end"
                control={
                    <Switch
                        checked={!!isVacant}
                        onChange={(_, checked) =>
                            form.setValue(EPropertyFormField.IsVacant, checked, { shouldValidate: true })
                        }
                        size="small"
                        data-testid="header-is-vacant-switch"
                    />
                }
                label={<Typography variant="body2">Vacant</Typography>}
            />
        </Flex>
    );
}
